// PLAT-3373 — account holder date of birth: masking, validation and the wire format.
//
// Two representations, deliberately kept apart. The participant sees and types the US display
// form MM/DD/YYYY; AVS and Socure take the ISO 8601 calendar date YYYY-MM-DD. Only the display
// form is ever held in the form model, and the ISO conversion happens once at submit, so a
// half-typed date can never be mistaken for a wire value.

/** Minimum age for an account holder. */
const MINIMUM_AGE_YEARS = 18;

/** A typo'd year (e.g. 0985) rather than a supercentenarian; matches the BFF's own floor. */
const MAXIMUM_AGE_YEARS = 120;

const DISPLAY_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

export const useDateOfBirth = () => {

    /**
     * Progressive MM/DD/YYYY mask. Keeps digits only and inserts the slashes as the participant
     * types, so the field reads correctly mid-entry and cannot accumulate stray separators.
     * Extra digits beyond eight are dropped rather than silently reformatting what came before.
     */
    const maskDateOfBirth = (val: string): string => {
        const digits = (val ?? '').replace(/\D/g, '').slice(0, 8);
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    };

    /**
     * The date the display string names, or null when it is incomplete or not a real calendar
     * date. Date's constructor rolls overflow forward — new Date(1985, 1, 30) silently becomes
     * 2 March — so each component is read back and compared. Without that round-trip 02/30/1985
     * would pass as a valid date and reach Socure as the wrong day.
     */
    const parseDateOfBirth = (val: string): Date | null => {
        const match = DISPLAY_PATTERN.exec((val ?? '').trim());
        if (!match) return null;

        const [, month, day, year] = match;
        const monthIndex = Number(month) - 1;
        const dayNumber = Number(day);
        const yearNumber = Number(year);

        const parsed = new Date(yearNumber, monthIndex, dayNumber);
        const isRealDate = parsed.getFullYear() === yearNumber
            && parsed.getMonth() === monthIndex
            && parsed.getDate() === dayNumber;

        return isRealDate ? parsed : null;
    };

    /** Whole years elapsed, counting a birthday that has not yet come round this year as not reached. */
    const ageInYears = (birthDate: Date, today: Date): number => {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDelta = today.getMonth() - birthDate.getMonth();
        if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birthDate.getDate())) {
            age -= 1;
        }
        return age;
    };

    /**
     * The inline error for a display-form date of birth, or null when it is acceptable. Required
     * is the caller's concern only insofar as an empty value returns the required message; callers
     * that do not require the field should not call this at all.
     */
    const validateDateOfBirth = (val: string): string | null => {
        const trimmed = (val ?? '').trim();
        if (!trimmed) return 'Date of birth is required';

        const parsed = parseDateOfBirth(trimmed);
        if (!parsed) return 'Enter a valid date of birth as MM/DD/YYYY';

        // Midnight local, so a birthday today compares as a whole day rather than by clock time.
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (parsed > today) return 'Date of birth cannot be in the future';

        const age = ageInYears(parsed, today);
        if (age < MINIMUM_AGE_YEARS) return `Account holder must be at least ${MINIMUM_AGE_YEARS} years old`;
        if (age > MAXIMUM_AGE_YEARS) return 'Enter a valid date of birth as MM/DD/YYYY';

        return null;
    };

    /**
     * The ISO 8601 calendar date for the wire, or null when the display value is not a usable
     * date. Built from the display components rather than via toISOString(), which converts to
     * UTC and would shift a birth date back a day for anyone west of Greenwich.
     */
    const toIsoDateOfBirth = (val: string): string | null => {
        const match = DISPLAY_PATTERN.exec((val ?? '').trim());
        if (!match || !parseDateOfBirth(val)) return null;

        const [, month, day, year] = match;
        return `${year}-${month}-${day}`;
    };

    /** The display form for a stored ISO date, so a resumed session refills the field. */
    const fromIsoDateOfBirth = (iso: string | null | undefined): string => {
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso ?? '').trim());
        if (!match) return '';

        const [, year, month, day] = match;
        return `${month}/${day}/${year}`;
    };

    return { maskDateOfBirth, parseDateOfBirth, validateDateOfBirth, toIsoDateOfBirth, fromIsoDateOfBirth };
};

export default useDateOfBirth;
